class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Function to refresh the cart drawer
async function refreshCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer, [id*='cart-drawer'], [class*='cart-drawer']");
  if (!cartDrawer) return;

  try {
    const html = await (await fetch("/cart?section_id=cart-drawer")).text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newInner = doc.querySelector(".drawer__inner, .cart-drawer__content, [data-cart-drawer-content]");
    const currentInner = cartDrawer.querySelector(".drawer__inner, .cart-drawer__content, [data-cart-drawer-content]");

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
      cartDrawer.classList.remove("is-empty");
      customElements?.upgrade?.(cartDrawer);
    }
  } catch (err) {
    console.error("Error refreshing cart drawer:", err);
  }
}

// Function to open the cart drawer
function openCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;
  
  if (typeof cartDrawer.open === "function") {
    cartDrawer.open();
  } else {
    cartDrawer.classList.add("is-open", "active", "open");
    document.body.classList.add("overflow-hidden");
  }
}

// Listener for iframe configurator messages
window.addEventListener('message', async (event) => {
  if (event.data?.type === 'addToCartFromConfigurator') {
    const { products } = event.data.data;

    try {
      // Add all products at once
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: products.map((p) => ({
            id: p.id,
            quantity: p.quantity,
            properties: {
              ...(p.properties || {}),
              Customisation: "True",
            },
          })),
        }),
      });

      // Close iframe overlay
      const iframeOverlay = document.getElementById('iframeOverlay');
      const iframe = document.getElementById('customizeIframe');
      if (iframeOverlay && iframe) {
        iframeOverlay.style.display = 'none';
        // iframe.src = '';
        document.body.style.overflow = '';
         setTimeout(() => {
          iframe.src = iframe.src; 
        }, 100);
      }

      // Refresh and open cart drawer
      setTimeout(async () => {
        await refreshCartDrawer();
        openCartDrawer();
      }, 50);

    } catch (error) {
      console.error('Error adding products to cart:', error);
    }
  }
});

// Auto-close cart drawer when cart is empty
(() => {

  const getDrawer = () =>
    document.querySelector('cart-drawer, .cart-drawer, #CartDrawer, [data-cart-drawer]');

  const closeDrawer = (drawer) => {
    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      drawer.querySelector('.cart-drawer__close, .drawer__close, [data-close-cart]')?.click();
      drawer.classList.remove('is-open', 'open', 'active');
      document.body.classList.remove('drawer-open');
    }
  };

  // Close drawer if no cart items exist
  const checkCart = (cart) => {
    if (!cart || cart.item_count === 0) {
      closeDrawer(getDrawer());
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = getDrawer();
    if (!drawer) return;

    // Watch for DOM changes (cart item removed manually)
    new MutationObserver(() => {
      if (!drawer.querySelector('.cart-item, [data-cart-item]')) {
        closeDrawer(drawer);
      }
    }).observe(drawer, { childList: true, subtree: true });
  });

  // Shopify event fires when cart updates
  document.addEventListener('cart:updated', (event) => {
    const cart = event?.detail?.cart;
    checkCart(cart);
  });

})();
